import {useApp} from "../app.context";
import {useIntegrator} from "../integrator.context";
import AssetImage from "./assetImage.element";
import Checkmark from "./checkmark";
import {isAttributeCollection} from "../commands/metadataValue.command";
import {findMetadata} from "../utils/metadata.utils";
import {useNavTarget} from "./useNavTarget";
import {NavTarget} from "../utils/navTarget";

interface AttributeLabelProps {
    uid: string;
    suffix?: string;
    // When non-empty, decorates the label with a warning icon and red border;
    // the string becomes the hover tooltip. Used for unmet dependencies.
    warning?: string;
    onDelete?(): void;
}

function MetadataLabel(props: AttributeLabelProps) {
    const {setNotification, getLocalizationValue, getTypeDescriptionKey, confirmDelete} = useApp();
    const {state} = useIntegrator();
    const locKey = props.uid;
    // One scan yields both the description id and the collection. The collection
    // decides whether this label shows an icon: attribute collections (unit/
    // building attributes and unit types) don't. Unknown uids keep the icon.
    const meta = findMetadata(state, props.uid);
    const descKey = meta ? getTypeDescriptionKey(props.uid, meta.id) : `${props.uid}.description`;
    const showIcon = !meta || !isAttributeCollection(meta.collection);
    // Ctrl/Cmd-click navigates to this metadata's editor (no-op for unknown uids).
    const navTarget: NavTarget | undefined = meta ? {kind: 'metadata', uid: props.uid} : undefined;

    const hasWarning = !!props.warning;

    return (
        <div className={'layout horizontal center stretch gap label'}
             {...useNavTarget(navTarget)}
             title={props.warning}
             style={hasWarning ? {border: 'solid 1px #ff6666', borderRadius: 'var(--border-radius)'} : undefined}>
            <div className={'layout horizontal gap center'}>
                {showIcon && <div style={{position: 'relative'}}>
                    <AssetImage id={props.uid}/>
                    <Checkmark id={props.uid} style={{position: 'absolute', top: 2, right: 2}}/>
                </div>}
                <p style={{width: 160, padding: 5}}
                   onClick={() => setNotification({title: locKey, message: descKey, edit: true})}
                >
                    {getLocalizationValue(locKey)}{props.suffix ?? ''}
                </p>
                {hasWarning && <span className={'fa-solid fa-triangle-exclamation'}
                                     style={{color: '#ff6666', marginRight: 6}}/>}
            </div>
            {props.onDelete && <button style={{marginRight: 8}}>
                <span className={'fa-solid fa-trash'} style={{color: 'white'}} onClick={async (e) => {
                    e.stopPropagation();
                    if (props.onDelete && await confirmDelete(getLocalizationValue(locKey))) {
                        props.onDelete();
                    }
                }}/>
            </button>}
        </div>
    )
}

export default MetadataLabel;